import axios from "axios";

import { EXTENSION_IDENTIFIER, ORG_VARS, orgVarName } from "./orgVariables";

export { EXTENSION_IDENTIFIER };

/**
 * All REST calls to the Easy Pluginz backends live here.
 * `auth` is always `{ orgId, apiKey }`.
 */

// Fall back to production URLs so a build env missing a variable can never
// produce literal "undefined/..." request URLs (CRA bakes these in at build time).
const ADMIN_SERVER_URL =
  process.env.REACT_APP_ADMIN_SERVER_URL ||
  "https://api.easy-pluginz.com/admin/v2";
const ADMIN_SERVER_V3_URL =
  process.env.REACT_APP_ADMIN_SERVER_V3_URL ||
  "https://api-lb.easy-pluginz.com.au/admin/v3";
const DB_SYNC_URL =
  process.env.REACT_APP_DBURL ||
  "https://api.easy-pluginz.com/dbsync/mysql/v2";

export const ZOHO_CONN_NAME = `${EXTENSION_IDENTIFIER}__zoho`;

export const ZOHO_OAUTH_SCOPES = [
  "ZohoCRM.users.CREATE",
  "ZohoCRM.users.READ",
  "ZohoCRM.users.UPDATE",
  "ZohoCRM.org.READ",
  "ZohoCRM.org.UPDATE",
  "ZohoCRM.settings.CREATE",
  "ZohoCRM.settings.READ",
  "ZohoCRM.settings.UPDATE",
  "ZohoCRM.coql.READ",
  "ZohoCRM.modules.CREATE",
  "ZohoCRM.modules.READ",
  "ZohoCRM.modules.UPDATE",
];

const authHeaders = ({ orgId, apiKey }) => ({
  orgid: orgId,
  apikey: apiKey,
  connname: EXTENSION_IDENTIFIER,
});

/** Execute a previously built axios request config (e.g. from buildGrabRecordsRequest). */
export const runRequest = (config) => axios.request(config);

/* ------------------------------------------------------------------ */
/* Zoho OAuth (Admin server)                                           */
/* ------------------------------------------------------------------ */

export const accountsUrlFromDataCenterUrl = (dataCenterUrl) => {
  // Canada uses the zohocloud.ca domain for accounts, unlike every other DC
  if (dataCenterUrl === "https://www.zohoapis.ca") {
    return "https://accounts.zohocloud.ca";
  }
  return (
    "https://accounts.zoho." + dataCenterUrl?.split("https://www.zohoapis.")?.[1]
  );
};

export const buildZohoAuthHeaders = ({ orgId, apiKey, dataCenterUrl }) => ({
  apikey: apiKey,
  connname: ZOHO_CONN_NAME,
  orgid: orgId,
  accountsurl: dataCenterUrl,
  dataCenterurlvariablename: orgVarName(ORG_VARS.DATA_CENTER_URL),
});

export const verifyZohoAuth = async (headers) => {
  const resp = await axios({
    url: `${ADMIN_SERVER_URL}/auth/zoho/verifyauth`,
    headers,
  });
  return resp?.data?.org?.length > 0;
};

export const requestZohoAuthUrl = async ({
  orgId,
  apiKey,
  zapiKey,
  dataCenterUrl,
  zuid,
}) => {
  const resp = await axios.request({
    url: `${ADMIN_SERVER_URL}/auth/zoho/authenticate`,
    method: "POST",
    data: {
      orgId,
      apiKey,
      zapiKey,
      accountsUrl: accountsUrlFromDataCenterUrl(dataCenterUrl),
      connName: ZOHO_CONN_NAME,
      scope: ZOHO_OAUTH_SCOPES,
      redirectUrl: `${ADMIN_SERVER_URL}/auth/zoho/callback`,
      crmApiDomain: dataCenterUrl,
      zuid,
    },
  });
  return resp?.data?.authUrl;
};

export const revokeZohoAuth = async ({ orgId, apiKey }) => {
  const resp = await axios.request({
    url: `${ADMIN_SERVER_URL}/auth/zoho/revoke`,
    method: "GET",
    headers: { orgid: orgId, apikey: apiKey, connname: ZOHO_CONN_NAME },
  });
  return resp?.data;
};

export const createExtensionWidget = async ({
  orgId,
  apiKey,
  dataCenterUrl,
  widget,
}) => {
  const resp = await axios.request({
    url: `${ADMIN_SERVER_URL}/data/zoho/crm/extwidgets`,
    method: "POST",
    data: { ...widget, apiDomain: dataCenterUrl },
    headers: {
      apikey: apiKey,
      connname: ZOHO_CONN_NAME,
      datacenterurl: dataCenterUrl,
      orgid: orgId,
    },
  });
  return resp.data;
};

/* ------------------------------------------------------------------ */
/* Sync settings (Admin server)                                        */
/* ------------------------------------------------------------------ */

export const getAllSettings = async (auth) => {
  const resp = await axios.request({
    url: `${ADMIN_SERVER_URL}/db/planetscale/clients/settings`,
    method: "GET",
    headers: authHeaders(auth),
  });
  return resp.data.data;
};

export const getSettingsByModule = async (auth, moduleApiName) => {
  const resp = await axios.request({
    url: `${ADMIN_SERVER_URL}/db/planetscale/clients/settings?moduleApiName=${moduleApiName}`,
    method: "GET",
    headers: authHeaders(auth),
  });
  return resp.data.data;
};

export const saveSetting = async (auth, { moduleApiName, settingSchema, settingId }) => {
  const resp = await axios.request({
    url: `${ADMIN_SERVER_URL}/db/planetscale/clients/settings`,
    method: "POST",
    data: settingId
      ? { moduleApiName, settingId, settingSchema }
      : { moduleApiName, settingSchema },
    headers: authHeaders(auth),
  });
  return resp.data;
};

export const deleteSetting = async (auth, settingId) => {
  const resp = await axios.request({
    url: `${ADMIN_SERVER_URL}/db/planetscale/clients/settings/${settingId}`,
    method: "DELETE",
    headers: authHeaders(auth),
  });
  return resp.data;
};

/** Rows come back with `setting_schema` as a JSON string and include the
 *  ConnectionSchema row; return real module settings with parsed schemas. */
export const parseSettingRows = (rows) =>
  (rows || [])
    .filter((row) => row.module_name !== "ConnectionSchema")
    .map((row) => ({ ...row, setting_schema: JSON.parse(row.setting_schema) }));

/* ------------------------------------------------------------------ */
/* Cron jobs v3 — cronjoborg (Admin server v3)                         */
/*                                                                     */
/* The current generation of background jobs. The scheduler pings the  */
/* job's `url` when `execute_in` minutes have elapsed, appending        */
/* `&job_id=`; the invoked Deluge function reads/updates the job and   */
/* DELETES it when done — a job's existence is its "In Progress" status.*/
/* mysqlsync-dedicated path so the shared cron store is untouched.      */
/* ------------------------------------------------------------------ */

const CRONJOBORG_PATH = `${ADMIN_SERVER_V3_URL}/utils/mysqlsync/cronjoborg`;

export const getCronJobsOrg = async (auth) => {
  const resp = await axios.request({
    url: CRONJOBORG_PATH,
    method: "GET",
    headers: authHeaders(auth),
  });
  return resp.data.data;
};

export const createCronJobOrg = async (auth, data) => {
  const resp = await axios.request({
    url: CRONJOBORG_PATH,
    method: "POST",
    data,
    headers: authHeaders(auth),
  });
  return resp.data;
};

export const deleteCronJobOrg = async (auth, jobId) => {
  const resp = await axios.request({
    url: `${CRONJOBORG_PATH}/${jobId}`,
    method: "DELETE",
    headers: authHeaders(auth),
  });
  return resp.data;
};

/** "yyyy-MM-dd HH:mm:ss+HH:mm" — the lastFetchedTime format the Deluge worker parses. */
export const nowSyncTimestamp = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  const offsetMinutes = -d.getTimezoneOffset();
  const sign = offsetMinutes >= 0 ? "+" : "-";
  const abs = Math.abs(offsetMinutes);
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ` +
    `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}` +
    `${sign}${pad(Math.floor(abs / 60))}:${pad(abs % 60)}`
  );
};

/** v3 job payload that makes the scheduler invoke one of this extension's CRM functions.
 *  The job name is ALWAYS `{orgid}__{connname}` — the backend matches on it. */
export const buildSyncJobOrg = ({
  auth,
  dataCenterUrl,
  zapiKey,
  functionName = "syncmoduledata",
  apiVersion = "v2", // newer functions (e.g. backfillmoduledata) execute on v7
  body,
  executeIn,
}) => ({
  url: `${dataCenterUrl}/crm/${apiVersion}/functions/${EXTENSION_IDENTIFIER}__${functionName}/actions/execute?auth_type=apikey&zapikey=${zapiKey}`,
  headers: {
    orgid: auth.orgId,
    apikey: auth.apiKey,
    // the v3 list endpoint scopes jobs by headers.orgid + headers.connname —
    // jobs stored without connname would be invisible to the org's job list
    connname: EXTENSION_IDENTIFIER,
    datacenterurl: dataCenterUrl,
  },
  body,
  // the cron runner appends &job_id= to the url itself, so params carries only execute_in
  params: { execute_in: executeIn },
  type: "GET",
  name: `${auth.orgId}__${EXTENSION_IDENTIFIER}`,
  execute_in: executeIn,
});

/* ------------------------------------------------------------------ */
/* DB Sync server (customer's MySQL)                                   */
/* ------------------------------------------------------------------ */

export const verifyDbConnection = async (auth, connectionUrl) => {
  const resp = await axios.request({
    url: `${DB_SYNC_URL}/verify`,
    method: "POST",
    headers: authHeaders(auth),
    data: { Connection_Url: connectionUrl },
  });
  return resp.data;
};

export const createModuleTable = async (auth, moduleApiName, connectionUrl) => {
  const resp = await axios.request({
    url: `${DB_SYNC_URL}/setting/${moduleApiName}`,
    method: "POST",
    data: {},
    headers: { ...authHeaders(auth), connectionUrl },
  });
  return resp.data;
};

export const deleteModuleTable = async (auth, moduleApiName, connectionUrl) => {
  const resp = await axios.request({
    url: `${DB_SYNC_URL}/setting/${moduleApiName}`,
    method: "DELETE",
    headers: { ...authHeaders(auth), connectionUrl },
  });
  return resp.data;
};

export const countTotalRecords = async (
  auth,
  { moduleName, timeRange, connectionUrl }
) => {
  const resp = await axios.request({
    url: `${DB_SYNC_URL}/count_total_record`,
    method: "POST",
    data: { timeRange, moduleName },
    headers: { ...authHeaders(auth), connectionUrl },
  });
  return resp.data;
};

/** Request config only — DataBackup stores these per chunk and runs them on demand. */
export const buildGrabRecordsRequest = (
  auth,
  { moduleName, timeRange, limit, offset, connectionUrl }
) => ({
  url: `${DB_SYNC_URL}/grab_records_based_on_time`,
  method: "POST",
  data: { timeRange, moduleName, limit, offset },
  headers: { ...authHeaders(auth), connectionUrl },
});

export const fetchRecordHistory = async (
  auth,
  { moduleName, recordId, connectionUrl }
) => {
  const resp = await axios.request({
    url: `${DB_SYNC_URL}/${moduleName}/${recordId}`,
    method: "GET",
    headers: { ...authHeaders(auth), connectionUrl },
  });
  return resp.data;
};
